const {
  findTokenPhrase,
  normalizeUnderstandingText,
} = require("./textUnderstanding");

const SAFETY_FUZZY_TOKENS = new Set([
  "everyone",
  "would",
  "better",
  "without",
  "miss",
  "should",
]);

const SAFETY_ALIASES = {
  off: ["of"],
};

const NON_SAFETY_HERE_CONTEXT = new Set([
  "meeting",
  "call",
  "class",
  "workshop",
  "room",
  "office",
  "event",
]);

const PRACTICAL_CLARIFICATION_PATTERN = /\b(i meant|i mean|talking about|meant)\b.{0,80}\b(shops?|shopping|errand|work|working|work call|meeting|call|class|task|tired|exhausted|this situation|this project)\b/i;
const PRACTICAL_CONTEXT_PATTERN = /\b(this|the|that|my|a)\s+(meeting|task|shopping|work\s+call|call|class|workshop|project|situation|errand|work)\b/i;
const PRACTICAL_HERE_PATTERN = /\b(do not want to be here|don't want to be here|dont want to be here)\b.{0,50}\b(meeting|call|workshop|class|task|work|shopping)\b/i;
const PROTECTED_SAFETY_PATTERN = /\b(kill myself|end my life|take my own life|suicidal|suicide|self[-\s]?harm|harm myself|hurt myself|i(?: am| m)? not safe|unsafe right now|in immediate danger|everyone would be better off without me|better off without me|no reason to live|life is(?:n't| not) worth living|i want to hurt myself)\b/i;
const SAFETY_REFERENCE_PATTERN = /\b(suicid(?:e|al)|self[-\s]?harm|kill(?:ed|ing)? (?:myself|himself|herself|themselves)|end(?:ed|ing)? (?:my|his|her|their) life|take (?:my|his|her|their) own life|harm(?:ed|ing)? (?:myself|himself|herself|themselves)|hurt(?:ing)? (?:myself|himself|herself|themselves)|want(?:s|ed)? to die|want to disappear|wish(?:es|ed)? (?:i|he|she|they) (?:wasn'?t|wasn t|were not) here|what (?:is|s) the point of living|no reason to live|life is(?:n'?t|n t| not) worth living|better off without me|not safe|unsafe right now|in immediate danger|no hope|nothing left|barely holding on|can(?:not|'?t) go on)\b/i;
const THIRD_PERSON_SUBJECT_SOURCE = "(?:friends?|siblings?|brothers?|sisters?|mothers?|fathers?|parents?|relatives?|cousins?|partners?|colleagues?|clients?|neighbou?rs?|children?|sons?|daughters?|spouses?|husbands?|wives?|classmates?|someone|somebody|another person|the person|the member|the client|the cancer sufferer|one younger sibling|one older sibling|he|she|they)";
const THIRD_PERSON_EXPLICIT_RISK_PATTERN = new RegExp(
  `\\b${THIRD_PERSON_SUBJECT_SOURCE}\\b.{0,100}\\b(?:` +
    "(?:is|are|was|were|feels?|felt|seems?|seemed|became|remains?|has been|have been|had been)\\s+(?:actively\\s+)?(?:suicidal|unsafe|not safe|in immediate danger|hopeless)|" +
    "(?:has|have|had|keeps?|reports?|reported|says?|said)\\s+(?:he\\s+|she\\s+|they\\s+)?(?:suicidal thoughts?|thoughts? (?:about|of) suicide|a suicide plan|no hope|nothing left)|" +
    "(?:wants?|wanted|plans?|planned|intends?|intended|might|may|could|attempted|tries?|tried)\\s+(?:to\\s+)?(?:die|harm (?:himself|herself|themselves)|hurt (?:himself|herself|themselves)|kill (?:himself|herself|themselves)|attempt suicide)|" +
    "(?:committed|completed|attempted) suicide|died by suicide|killed (?:himself|herself|themselves)|ended (?:his|her|their) life|took (?:his|her|their) own life|" +
    "(?:can(?:not|'?t) go on|barely holding on)" +
  ")\\b",
  "i"
);
const THIRD_PERSON_CURRENT_RISK_PATTERN = new RegExp(
  `\\b${THIRD_PERSON_SUBJECT_SOURCE}\\b.{0,100}\\b(?:` +
    "(?:is|are|feels?|seems?|remains?|has been|have been)\\s+(?:actively\\s+)?(?:suicidal|unsafe|not safe|in immediate danger|hopeless)|" +
    "(?:has|have|keeps?|reports?|says?)\\s+(?:he\\s+|she\\s+|they\\s+)?(?:suicidal thoughts?|thoughts? (?:about|of) suicide|a suicide plan|no hope|nothing left)|" +
    "(?:wants?|plans?|intends?|might|may|could)\\s+(?:to\\s+)?(?:die|harm (?:himself|herself|themselves)|hurt (?:himself|herself|themselves)|kill (?:himself|herself|themselves))" +
  ")\\b",
  "i"
);
const THIRD_PERSON_CURRENT_ATTEMPT_PATTERN = new RegExp(
  `(?:\\b${THIRD_PERSON_SUBJECT_SOURCE}\\b.{0,120}\\b(?:` +
    "(?:(?:has|have)\\s+)?(?:just|recently)\\s+attempted suicide|" +
    "attempted suicide\\b.{0,40}\\b(?:today|tonight|just now|moments? ago|minutes? ago|hours? ago|this (?:morning|afternoon|evening)|recently)" +
  ")|\\b(?:earlier today|today|tonight|just now|moments? ago|minutes? ago|hours? ago|this (?:morning|afternoon|evening)|recently)\\b.{0,60}\\b" +
    `${THIRD_PERSON_SUBJECT_SOURCE}\\b.{0,80}\\b(?:attempted suicide|(?:(?:has|have)\\s+)?(?:just|recently)\\s+attempted suicide))\\b`,
  "i"
);
const REPORTED_SPEECH_PATTERN = /\b(?:friends?|siblings?|brothers?|sisters?|mothers?|fathers?|parents?|relatives?|cousins?|partners?|colleagues?|clients?|neighbou?rs?|children?|sons?|daughters?|spouses?|husbands?|wives?|classmates?|someone|somebody|he|she|they)\b.{0,60}\b(?:said|says|told|tells|wrote|writes|texted|texts|reported|reports|mentioned|mentions|asked|asks|shared|shares)\b/i;
const REPORTED_FIRST_PERSON_SAFETY_PATTERN = new RegExp(
  `\\b${THIRD_PERSON_SUBJECT_SOURCE}\\b.{0,60}\\b` +
    "(?:said|says|told|tells|wrote|writes|texted|texts|reported|reports|quoted|reads?)(?:\\s+(?:to\\s+)?me)?\\s+(?:that\\s+)?i\\b.{0,60}\\b" +
    "(?:suicid(?:e|al)|self[-\\s]?harm|kill myself|end my life|take my own life|harm myself|hurt myself|want to die|want to disappear|not safe|no reason to live|better off without me|no one would miss me|nobody would miss me|cannot go on|what (?:is|s) the point of living|life is not worth living|wish i (?:was not|were not) here)\\b",
  "i"
);
const INFORMATIONAL_REPORTED_FIRST_PERSON_SAFETY_PATTERN = /\b(?:article|book|film|movie|report|study|character|message)\b.{0,60}\b(?:said|says|wrote|writes|reported|reports|quoted|reads?)(?:\s+(?:to\s+)?me)?\s+(?:that\s+)?i\b.{0,60}\b(?:suicid(?:e|al)|self[-\s]?harm|kill myself|end my life|take my own life|harm myself|hurt myself|want to die|not safe|no reason to live|better off without me)\b/i;
const HISTORICAL_CONTEXT_PATTERN = /\b(?:years? ago|months? ago|weeks? ago|last (?:week|month|year)|a long time ago|in the past|previously|formerly|back then|when (?:i|he|she|they) (?:was|were|had been)|as a child|during childhood|in (?:19|20)\d{2}|used to|no longer|not anymore|historical(?:ly)?|late sibling|died by suicide|committed suicide|completed suicide|killed (?:himself|herself|themselves)|ended (?:his|her|their) life|took (?:his|her|their) own life)\b/i;
const CURRENT_SAFETY_CONTEXT_PATTERN = /(?:\b(?:now|right now|today|tonight|currently|still|lately|at the moment|this moment)\b.{0,80}\b(?:suicidal|self[-\s]?harm|want(?:s)? to (?:die|harm|hurt|kill)|not safe|unsafe|in danger)\b|\b(?:suicidal|self[-\s]?harm|want(?:s)? to (?:die|harm|hurt|kill)|not safe|unsafe|in danger)\b.{0,40}\b(?:now|right now|today|tonight|currently|still|lately|at the moment|this moment)\b)/i;
const FIRST_PERSON_SAFETY_PATTERN = /\b(?:i\s+(?:am|m|was|were|have been|feel|felt|think|thought|sometimes think|keep thinking|remember|remembered|recall|recalled|worry|worried|want|wanted|wish|wished|plan|planned|might|may|could|consider|considered|attempted|tried|used to)|my\s+(?:suicidal|self[-\s]?harm)|myself)\b.{0,100}\b(?:suicid(?:e|al)|self[-\s]?harm|kill myself|end my life|take my own life|harm myself|hurt myself|want to die|want to disappear|cannot go on|not safe|unsafe|in danger|no hope|nothing left|barely holding on|no reason to live|point of living|life is not worth living)\b/i;
const INFORMATIONAL_SAFETY_PATTERN = /(?:\b(?:article|book|film|movie|news|research|statistics?|policy|training|handbook|role[-\s]?play|prevention|awareness campaign|study|report)\b.{0,100}\b(?:suicid(?:e|al)|self[-\s]?harm)\b|\b(?:suicid(?:e|al)|self[-\s]?harm)\b.{0,100}\b(?:article|book|film|movie|news|research|statistics?|policy|training|handbook|role[-\s]?play|prevention|awareness campaign|study|report)\b)/i;
const MEDIA_SAFETY_REFERENCE_PATTERN = /(?:\b(?:article|book|film|movie|news|research|study|report|training|handbook|role[-\s]?play|character|fictional|example|quote|quotation)\b.{0,140}\b(?:suicid(?:e|al)|self[-\s]?harm|want(?:s|ed)? to die|kill(?:ed|ing)? (?:myself|himself|herself|themselves)|harm(?:ed|ing)? (?:myself|himself|herself|themselves)|hurt(?:ing)? (?:myself|himself|herself|themselves)|not safe|no reason to live)\b|\b(?:suicid(?:e|al)|self[-\s]?harm|want(?:s|ed)? to die|not safe|no reason to live)\b.{0,140}\b(?:article|book|film|movie|news|research|study|report|training|handbook|role[-\s]?play|character|fictional|example|quote|quotation)\b)/i;
const QUOTED_NON_PERSONAL_CONTEXT_PATTERN = /\b(?:article|book|film|movie|news report|research paper|study|example|quote|quotation|character|story|message)\b.{0,80}\b(?:said|says|reads?|quoted|included|contains?|was|is)\b.{0,80}\b(?:i|he|she|they)\b.{0,50}\b(?:suicid(?:e|al)|self[-\s]?harm|kill|die|harm|hurt)\b/i;
const PROFESSIONAL_INFORMATIONAL_SAFETY_PATTERN = /\b(?:assess(?:ing|ment)?|screen(?:ing)?|writing notes?|document(?:ing|ation)?|case notes?)\b.{0,100}\b(?:a |my |the )?clients?\b.{0,80}\b(?:suicid(?:e|al)|self[-\s]?harm)\b/i;
const INFORMATIONAL_QUOTE_CUE_PATTERN = /\b(?:article|book|film|movie|news|research|study|report|training|handbook|role[-\s]?play|character|fictional|example|quote|quotation)\b/i;
const EXPLICIT_NON_PERSONAL_SAFETY_CLARIFICATION_PATTERN = /\b(?:i (?:am|m) not (?:talking|speaking) about (?:me|myself)|i (?:am|m) not talking about my own safety|this is not about (?:me|myself)|not about (?:me|myself)|not talking about (?:me|myself)|this is about (?:support(?:ing)?(?: for)? )?(?:(?:my|one|two|three|some) )?(?:friends?|siblings?|brothers?|sisters?|parents?|relatives?|cousins?|partners?|colleagues?|clients?|someone else|another person)|i (?:am|m) talking about (?:support(?:ing)?(?: for)? )?(?:(?:my|one|two|three|some) )?(?:friends?|siblings?|brothers?|sisters?|parents?|relatives?|cousins?|partners?|colleagues?|clients?|someone else|another person))\b/i;
const EXPLICIT_SAFETY_STATUS_PATTERN = /^(?:(?:yes|no|thanks|thank you) )?i (?:am|m) (?:fine|safe|okay|ok|not in danger)(?: (?:now|right now))?(?: (?:thanks|thank you))?$/i;
const EXPLICIT_SAFETY_STATUS_SENTENCE_PATTERN = /(?:^|[.!?]\s*)(?:(?:yes|no|thanks|thank you)[,\s]+)?i(?:'m| am) (?:fine|safe|okay|ok|not in danger)(?:\s+(?:now|right now))?(?:\s+(?:thanks|thank you))?(?=[.!?]|$)/i;
const EXPLICIT_NEGATED_PERSONAL_SAFETY_PATTERN = /(?:^|[.!?]\s*)(?:(?:no|yes|thanks|thank you|to be clear)[,\s]+)?(?:i(?:'m| am) not (?:suicidal|unsafe|in danger|going to (?:die|kill myself|harm myself|hurt myself)|planning to (?:die|kill myself|harm myself|hurt myself)|thinking about (?:suicide|killing myself|harming myself|hurting myself))|i (?:do not|don't) (?:want|intend|plan) to (?:die|kill myself|harm myself|hurt myself)|i (?:do not|don't) have (?:any )?(?:suicidal thoughts?|thoughts? (?:about|of) suicide|a suicide plan)|i have no (?:suicidal thoughts?|thoughts? (?:about|of) suicide|suicide plan))(?=[.!?,;]|$)/i;
const NEGATED_PERSONAL_RISK_CLAUSE_PATTERN = /\bi(?: am| m) not (?:suicidal|unsafe|in danger|going to (?:die|kill myself|harm myself|hurt myself)|planning to (?:die|kill myself|harm myself|hurt myself)|thinking about (?:suicide|killing myself|harming myself|hurting myself))\b|\bi (?:do not|don t) (?:want|intend|plan) to (?:die|kill myself|harm myself|hurt myself)\b|\bi (?:do not|don t) have (?:any )?(?:suicidal thoughts?|thoughts? (?:about|of) suicide|a suicide plan)\b|\bi have no (?:suicidal thoughts?|thoughts? (?:about|of) suicide|suicide plan)\b/gi;
const HISTORICAL_FIRST_PERSON_ATTEMPT_PATTERNS = [
  /\bi (?:attempted suicide|tried to (?:commit suicide|kill myself|harm myself|hurt myself))\b.{0,50}\b(?:years? ago|months? ago|weeks? ago|last (?:week|month|year)|in (?:19|20)\d{2}|in the past|previously|back then)\b/gi,
  /\b(?:years? ago|months? ago|weeks? ago|last (?:week|month|year)|in (?:19|20)\d{2}|in the past|previously|back then)\b.{0,50}\bi (?:attempted suicide|tried to (?:commit suicide|kill myself|harm myself|hurt myself))\b/gi,
];
const FIRST_PERSON_CURRENT_RISK_PATTERNS = [
  /\b(?:kill myself|end my life|take my own life|harm myself|hurt myself)\b/i,
  /\bi (?:am|m|feel|have been|ve been) (?:actively )?(?:suicidal|unsafe|not safe|in immediate danger)\b/i,
  /\bi (?:do not|don t) feel safe(?: now| right now)?\b/i,
  /\bi (?:was|felt) suicidal\b.{0,35}\b(?:today|tonight|just now|moments? ago|minutes? ago|hours? ago|this (?:morning|afternoon|evening)|recently)\b/i,
  /\b(?:today|tonight|just now|moments? ago|minutes? ago|hours? ago|this (?:morning|afternoon|evening)|recently)\b.{0,35}\bi (?:was|felt) suicidal\b/i,
  /\bi (?:(?:have|ve)(?: been having| had| got| made)?|am having|keep having|experience|made) (?:a |any )?(?:suicidal thoughts?|thoughts? (?:about|of) suicide|suicide plan|plan to (?:die|kill myself|harm myself|hurt myself))\b/i,
  /\bi (?:want|intend|might|may|could|am going|feel like|consider|am considering)\s+(?:to\s+|committing\s+)?(?:suicide(?! prevention| statistics| research| policy| training| awareness)|die|kill myself|end my life|harm myself|hurt myself)\b/i,
  /\bi (?:plan|am planning) (?:to|on) (?:commit(?:ting)? suicide|die|dying|kill(?:ing)? myself|end(?:ing)? my life|harm(?:ing)? myself|hurt(?:ing)? myself)\b/i,
  /\bi will (?:attempt|commit) suicide\b/i,
  /\b(?:sometimes )?i (?:think|keep thinking|am thinking) (?:about|of) (?:suicide(?! prevention| statistics| research| policy| training| awareness)|killing myself|harming myself|hurting myself|dying)\b/i,
  /\bsuicide (?:feels?|seems?) like my only (?:option|way out)\b/i,
  /\b(?:everyone|people|my family) would be better off without me\b/i,
  /\b(?:no one|nobody) would miss me\b/i,
  /\bi (?:cannot|can'?t) go on\b|\bi want to disappear\b|\bwhat(?:'s| is) the point of living\b|\blife is(?:n'?t| not) worth living\b|\bi wish i (?:wasn'?t|were not) here\b/i,
];
const FIRST_PERSON_HISTORICAL_RISK_PATTERN = /\b(?:i (?:was|felt|had been) suicidal|i used to (?:feel suicidal|think about suicide|self[-\s]?harm)|my (?:past|previous) suicide attempt)\b/i;
const FIRST_PERSON_ATTEMPT_RISK_PATTERN = /\bi (?:(?:have|ve) )?(?:just )?(?:attempted suicide|tried to (?:commit suicide|kill myself|harm myself|hurt myself))\b/i;
const RECENT_ATTEMPT_CONTEXT_PATTERN = /\b(?:today|tonight|just now|moments? ago|minutes? ago|hours? ago|this (?:morning|afternoon|evening)|recently)\b/i;
const CUMULATIVE_SAFETY_ADJACENT_SIGNALS = [
  { key: "no_escape", pattern: /\b(no escape|there is no escape|there'?s no escape|cannot escape|can't escape)\b/i },
  { key: "going_under", pattern: /\b(drowning|going under|go under|under the water|sink(?:ing)?)\b/i },
  { key: "edge", pattern: /\b(close to the edge|on the edge|at the edge|edge of collapse|edge of breaking)\b/i },
  { key: "hopeless", pattern: /\b(hopeless|no hope|never (?:feel )?(?:normal|hopeful)|not hopeful|nothing will change|new normal)\b/i },
  { key: "dark_endless", pattern: /\b(dark and endless|endless darkness|endless|dark feelings?|heavy and endless)\b/i },
  { key: "trapped", pattern: /\b(trapped|boxed in|stuck in this|cornered|no way out)\b/i },
  { key: "unbearable", pattern: /\b(unbearable|cannot bear|can't bear|too much to bear)\b/i },
  { key: "collapse", pattern: /\b(collapse|collapsing|breaking down|break down|fall apart|going to break)\b/i },
  { key: "invisible_risk", pattern: /\b(no one can see|nobody can see|no one sees|nobody sees)\b.{0,80}\b(close|going under|drowning|edge|collapse|breaking)\b/i },
];

function normalizeSafetyText(value = "") {
  return normalizeUnderstandingText(value);
}

function isNonSafetyHereContext(tokens = [], match = null) {
  if (!match) return false;
  const tail = tokens.slice(match.end, match.end + 4);
  if (!tail.length) return false;
  if (tail[0] !== "in" && tail[0] !== "at") return false;
  return tail.some((token) => NON_SAFETY_HERE_CONTEXT.has(token));
}

function hasTypoTolerantPassiveRiskSignal(text = "") {
  const normalized = normalizeSafetyText(text);
  if (!normalized) return false;
  const tokens = normalized.split(" ");
  const phrases = [
    ["everyone", "would", "be", "better", "off", "without", "me"],
    ["people", "would", "be", "better", "off", "without", "me"],
    ["my", "family", "would", "be", "better", "off", "without", "me"],
    ["no", "one", "would", "miss", "me"],
    ["nobody", "would", "miss", "me"],
    ["i", "should", "not", "be", "here"],
    ["i", "do", "not", "want", "to", "be", "here"],
  ];
  return phrases.some((phrase) => {
    const match = findTokenPhrase(tokens, phrase, {
      fuzzyTokens: SAFETY_FUZZY_TOKENS,
      aliases: SAFETY_ALIASES,
      maxEdits: 1,
    });
    if (!match) return false;
    if (phrase.join(" ") === "i do not want to be here" && isNonSafetyHereContext(tokens, match)) return false;
    return true;
  });
}

function hasProtectedSafetyWording(text = "") {
  const normalized = normalizeSafetyText(text);
  if (!normalized) return false;
  return PROTECTED_SAFETY_PATTERN.test(normalized) ||
    hasTypoTolerantPassiveRiskSignal(
      normalized.replace(/\bi do not want to be here(?: anymore)?\b/g, "")
    );
}

function emptySafetyClassification() {
  return {
    classification: "none",
    subject: "none",
    timeframe: "none",
    referenceType: "none",
    safetyReference: false,
    activePersonalSafety: false,
    otherPersonCurrentConcern: false,
    requiresClarification: false,
  };
}

function hasExplicitClarificationWording(normalized = "", raw = "") {
  return EXPLICIT_SAFETY_STATUS_PATTERN.test(normalized) ||
    EXPLICIT_SAFETY_STATUS_SENTENCE_PATTERN.test(raw) ||
    EXPLICIT_NEGATED_PERSONAL_SAFETY_PATTERN.test(raw) ||
    EXPLICIT_NON_PERSONAL_SAFETY_CLARIFICATION_PATTERN.test(normalized);
}

function stripNonCurrentPersonalRiskClauses(normalized = "") {
  let result = String(normalized || "").replace(NEGATED_PERSONAL_RISK_CLAUSE_PATTERN, " ");
  HISTORICAL_FIRST_PERSON_ATTEMPT_PATTERNS.forEach((pattern) => {
    pattern.lastIndex = 0;
    result = result.replace(pattern, " ");
  });
  return result.replace(/\s+/g, " ").trim();
}

function earliestPatternMatch(text = "", patterns = []) {
  return patterns.reduce((earliest, pattern) => {
    const match = text.match(pattern);
    if (!match || !Number.isFinite(match.index)) return earliest;
    return Math.min(earliest, match.index);
  }, Number.POSITIVE_INFINITY);
}

function stripAttributedQuotedSpeech(raw = "", normalized = "") {
  if (
    !REPORTED_SPEECH_PATTERN.test(normalized) &&
    !REPORTED_FIRST_PERSON_SAFETY_PATTERN.test(normalized) &&
    !INFORMATIONAL_REPORTED_FIRST_PERSON_SAFETY_PATTERN.test(normalized) &&
    !INFORMATIONAL_QUOTE_CUE_PATTERN.test(normalized)
  ) {
    return raw;
  }
  return String(raw)
    .replace(/["“][^"”]{0,500}["”]/g, " ")
    .replace(/(^|[^A-Za-z0-9])'([\s\S]{0,500}?)'(?![A-Za-z0-9])/g, "$1 ");
}

function attributedQuoteContents(raw = "") {
  const contents = [];
  String(raw).replace(/["“]([^"”]{0,500})["”]/g, (_match, content) => {
    contents.push(content);
    return _match;
  });
  String(raw).replace(/(^|[^A-Za-z0-9])'([\s\S]{0,500}?)'(?![A-Za-z0-9])/g, (_match, _prefix, content) => {
    contents.push(content);
    return _match;
  });
  return contents;
}

function hasAttributedQuotedSafetyWording(raw = "", normalized = "") {
  if (!REPORTED_SPEECH_PATTERN.test(normalized)) return false;
  return attributedQuoteContents(raw).some((content) => {
    const quote = normalizeSafetyText(content);
    return SAFETY_REFERENCE_PATTERN.test(quote) || hasTypoTolerantPassiveRiskSignal(quote);
  });
}

function segmentHasCurrentPersonalRisk(normalized = "") {
  const riskText = stripNonCurrentPersonalRiskClauses(normalized);
  const explicitPatternRisk = FIRST_PERSON_CURRENT_RISK_PATTERNS.some((pattern) => pattern.test(riskText));
  const currentAttempt = FIRST_PERSON_ATTEMPT_RISK_PATTERN.test(riskText) &&
    (!HISTORICAL_CONTEXT_PATTERN.test(riskText) || RECENT_ATTEMPT_CONTEXT_PATTERN.test(riskText));
  const typoRisk = hasTypoTolerantPassiveRiskSignal(riskText);
  if (explicitPatternRisk || currentAttempt) return true;
  if (typoRisk && hasPracticalContextClarification(riskText) && !hasProtectedSafetyWording(riskText)) return false;
  return typoRisk;
}

function hasDirectFirstPersonCurrentRisk(normalized = "", raw = "") {
  const withoutAttributedQuotes = normalizeSafetyText(stripAttributedQuotedSpeech(raw, normalized));
  if (withoutAttributedQuotes !== normalized) {
    return segmentHasCurrentPersonalRisk(withoutAttributedQuotes);
  }

  const attemptMatchIndex = FIRST_PERSON_ATTEMPT_RISK_PATTERN.test(normalized)
    ? normalized.search(FIRST_PERSON_ATTEMPT_RISK_PATTERN)
    : Number.POSITIVE_INFINITY;
  const patternMatchIndex = Math.min(
    earliestPatternMatch(normalized, FIRST_PERSON_CURRENT_RISK_PATTERNS),
    attemptMatchIndex
  );
  const currentRiskDetected = segmentHasCurrentPersonalRisk(normalized);
  if (!currentRiskDetected) return false;

  const reportedMatch = normalized.match(REPORTED_FIRST_PERSON_SAFETY_PATTERN) ||
    normalized.match(INFORMATIONAL_REPORTED_FIRST_PERSON_SAFETY_PATTERN);
  if (!reportedMatch || !Number.isFinite(reportedMatch.index)) return true;
  if (Number.isFinite(patternMatchIndex) && patternMatchIndex < reportedMatch.index) return true;

  const contrastSegments = normalized.split(/\b(?:but|however|whereas|while|and(?=\s+i\b))\b/i).slice(1);
  const laterSentenceSegments = String(raw)
    .split(/[.!?]\s+/)
    .slice(1)
    .map((segment) => normalizeSafetyText(segment));
  return [...contrastSegments, ...laterSentenceSegments].some(segmentHasCurrentPersonalRisk);
}

function hasActualThirdPersonRiskOutsideInformationalContext(raw = "", normalized = "") {
  const segments = String(raw)
    .split(/[.!?;]+|\b(?:but|however|separately|then|and now)\b/i)
    .map((segment) => normalizeSafetyText(segment))
    .filter(Boolean);
  const candidates = segments.length ? segments : [normalized];
  return candidates.some((segment) => {
    const informationalSegment = INFORMATIONAL_SAFETY_PATTERN.test(segment) ||
      MEDIA_SAFETY_REFERENCE_PATTERN.test(segment) ||
      QUOTED_NON_PERSONAL_CONTEXT_PATTERN.test(segment) ||
      INFORMATIONAL_REPORTED_FIRST_PERSON_SAFETY_PATTERN.test(segment) ||
      /\b(?:article|book|film|movie|news report|research paper|study|character|fictional|example|quotation)\b/i.test(segment);
    return !informationalSegment && (
      THIRD_PERSON_EXPLICIT_RISK_PATTERN.test(segment) ||
      REPORTED_FIRST_PERSON_SAFETY_PATTERN.test(segment) ||
      hasAttributedQuotedSafetyWording(raw, normalized)
    );
  });
}

function hasExplicitPersonalSafetyClarification(text = "") {
  const normalized = normalizeSafetyText(text);
  if (!normalized || !hasExplicitClarificationWording(normalized, String(text || ""))) return false;
  const classification = classifySafetyReference(text, { skipClarificationCheck: true });
  return classification.classification !== "first_person_current";
}

function classifySafetyReference(text = "", options = {}) {
  const raw = String(text || "").replace(/[’‘]/g, "'");
  const normalized = normalizeSafetyText(raw);
  if (!normalized) return emptySafetyClassification();

  if (hasDirectFirstPersonCurrentRisk(normalized, raw)) {
    return {
      classification: "first_person_current",
      subject: "first_person",
      timeframe: "current",
      referenceType: "direct_report",
      safetyReference: true,
      activePersonalSafety: true,
      otherPersonCurrentConcern: false,
      requiresClarification: false,
    };
  }

  const explicitClarification = hasExplicitClarificationWording(normalized, raw);

  if (hasPracticalContextClarification(normalized) && !hasProtectedSafetyWording(normalized)) {
    return emptySafetyClassification();
  }

  if (!SAFETY_REFERENCE_PATTERN.test(normalized) && !hasTypoTolerantPassiveRiskSignal(normalized)) {
    if (explicitClarification) {
      return {
        ...emptySafetyClassification(),
        classification: "explicit_deescalation",
        subject: "first_person",
        timeframe: "current",
        referenceType: "clarification",
      };
    }
    return emptySafetyClassification();
  }

  const reportedFirstPersonRisk = REPORTED_FIRST_PERSON_SAFETY_PATTERN.test(normalized) ||
    hasAttributedQuotedSafetyWording(raw, normalized);
  const reportedSpeech = REPORTED_SPEECH_PATTERN.test(normalized) || reportedFirstPersonRisk;
  const broadThirdPersonRisk = THIRD_PERSON_EXPLICIT_RISK_PATTERN.test(normalized) || reportedFirstPersonRisk;
  const informational = INFORMATIONAL_SAFETY_PATTERN.test(normalized) ||
    MEDIA_SAFETY_REFERENCE_PATTERN.test(normalized) ||
    QUOTED_NON_PERSONAL_CONTEXT_PATTERN.test(normalized) ||
    INFORMATIONAL_REPORTED_FIRST_PERSON_SAFETY_PATTERN.test(normalized) ||
    PROFESSIONAL_INFORMATIONAL_SAFETY_PATTERN.test(normalized);
  const thirdPerson = broadThirdPersonRisk && (
    !informational || hasActualThirdPersonRiskOutsideInformationalContext(raw, normalized)
  );
  const historical = HISTORICAL_CONTEXT_PATTERN.test(normalized) && !CURRENT_SAFETY_CONTEXT_PATTERN.test(normalized);

  if (informational && !thirdPerson) {
    return {
      ...emptySafetyClassification(),
      classification: "quoted_or_informational",
      subject: "none",
      timeframe: historical ? "historical" : "unknown",
      referenceType: "quoted_or_informational",
      safetyReference: true,
    };
  }

  if (thirdPerson) {
    const currentThirdPersonConcern = THIRD_PERSON_CURRENT_RISK_PATTERN.test(normalized) ||
      THIRD_PERSON_CURRENT_ATTEMPT_PATTERN.test(normalized) ||
      (reportedFirstPersonRisk && !historical);
    const thirdPersonHistorical = historical && !currentThirdPersonConcern;
    return {
      classification: thirdPersonHistorical ? "third_person_historical" : "third_person_current",
      subject: "third_person",
      timeframe: thirdPersonHistorical ? "historical" : "current",
      referenceType: reportedSpeech ? "reported_speech" : "direct_report",
      safetyReference: true,
      activePersonalSafety: false,
      otherPersonCurrentConcern: !thirdPersonHistorical,
      requiresClarification: false,
    };
  }

  if (informational) {
    return {
      ...emptySafetyClassification(),
      classification: "quoted_or_informational",
      subject: "none",
      timeframe: historical ? "historical" : "unknown",
      referenceType: "quoted_or_informational",
      safetyReference: true,
    };
  }

  if (explicitClarification) {
    return {
      ...emptySafetyClassification(),
      classification: "explicit_deescalation",
      subject: "first_person",
      timeframe: "current",
      referenceType: "clarification",
    };
  }

  const explicitFirstPersonPassive = /\b(?:i cannot go on|i want to disappear|what s the point of living|life isn t worth living|i wish i wasn t here|i wish i were not here)\b/i.test(normalized);
  const firstPersonHistorical = FIRST_PERSON_HISTORICAL_RISK_PATTERN.test(normalized) ||
    (FIRST_PERSON_ATTEMPT_RISK_PATTERN.test(normalized) && historical) ||
    (FIRST_PERSON_SAFETY_PATTERN.test(normalized) && historical);
  const firstPerson = firstPersonHistorical || FIRST_PERSON_SAFETY_PATTERN.test(normalized) ||
    /\b(?:kill myself|end my life|take my own life|harm myself|hurt myself|better off without me|i am not safe|unsafe right now)\b/i.test(normalized) ||
    explicitFirstPersonPassive ||
    hasTypoTolerantPassiveRiskSignal(normalized);
  if (firstPerson) {
    return {
      classification: firstPersonHistorical ? "first_person_historical" : "first_person_current",
      subject: "first_person",
      timeframe: firstPersonHistorical ? "historical" : "current",
      referenceType: "direct_report",
      safetyReference: true,
      activePersonalSafety: !firstPersonHistorical,
      otherPersonCurrentConcern: false,
      requiresClarification: firstPersonHistorical,
    };
  }

  return {
    classification: historical ? "ambiguous_historical" : "ambiguous",
    subject: "ambiguous",
    timeframe: historical ? "historical" : "unknown",
    referenceType: /["“”']/.test(raw) ? "quoted_speech" : "unclear",
    safetyReference: true,
    activePersonalSafety: false,
    otherPersonCurrentConcern: false,
    requiresClarification: true,
  };
}

function safetyUserMessages(messages = []) {
  return (Array.isArray(messages) ? messages : [])
    .map((entry, index) => ({
      entry,
      index,
      role: String(entry?.role || entry?.sender || "").toLowerCase(),
      text: textFromMessage(entry),
    }))
    .filter(({ role, text }) => role === "user" && normalizeSafetyText(text));
}

function getSafetyContextState(messages = []) {
  const entries = Array.isArray(messages) ? messages : [];
  const userMessages = safetyUserMessages(messages);
  let activePersonalSafety = false;
  let latestClassification = emptySafetyClassification();
  let lastPersonalTriggerIndex = -1;
  let lastDeescalationIndex = -1;
  let hasThirdPersonReference = false;
  let otherPersonCurrentConcern = false;
  let hasFirstPersonHistoricalReference = false;
  let lastNonPersonalSafetyReferenceIndex = -1;
  let latestNonPersonalSafetyClassification = "none";

  userMessages.forEach(({ text, index }) => {
    const classification = classifySafetyReference(text);
    const explicitClarification = hasExplicitPersonalSafetyClarification(text);
    if (classification.safetyReference || classification.classification === "explicit_deescalation") {
      latestClassification = classification;
    }
    if (classification.classification === "first_person_current") {
      activePersonalSafety = true;
      lastPersonalTriggerIndex = index;
    } else if (explicitClarification) {
      const priorWindowStart = Math.max(lastDeescalationIndex, lastNonPersonalSafetyReferenceIndex) + 1;
      const priorWindow = entries.slice(priorWindowStart, index);
      const recentPriorEntries = entries.slice(Math.max(priorWindowStart, index - 6), index);
      const priorSafetyResponse = recentPriorEntries
        .filter((entry) => String(entry?.role || entry?.sender || "").toLowerCase() === "assistant")
        .some((entry) => /\b(?:pause the coaching|immediate safety|are you safe right now|feel unsafe right now)\b/i.test(textFromMessage(entry)));
      const priorCumulativeConcern = Boolean(detectCumulativeSafetyAdjacentSignal(priorWindow));
      const hasResettableContext = activePersonalSafety ||
        lastPersonalTriggerIndex > lastDeescalationIndex ||
        lastNonPersonalSafetyReferenceIndex > lastDeescalationIndex ||
        priorSafetyResponse ||
        priorCumulativeConcern;
      if (hasResettableContext) {
        activePersonalSafety = false;
        lastDeescalationIndex = index;
      }
    }
    if (classification.subject === "third_person") {
      hasThirdPersonReference = true;
      otherPersonCurrentConcern = classification.otherPersonCurrentConcern;
      lastNonPersonalSafetyReferenceIndex = index;
      latestNonPersonalSafetyClassification = classification.classification;
    }
    if (classification.classification === "first_person_historical") {
      hasFirstPersonHistoricalReference = true;
      lastNonPersonalSafetyReferenceIndex = index;
      latestNonPersonalSafetyClassification = classification.classification;
    }
    if (classification.classification === "quoted_or_informational") {
      lastNonPersonalSafetyReferenceIndex = index;
      latestNonPersonalSafetyClassification = classification.classification;
    }
  });

  return {
    activePersonalSafety,
    classification: activePersonalSafety ? "first_person_current" : latestClassification.classification,
    latestClassification,
    explicitDeescalation: lastDeescalationIndex > lastPersonalTriggerIndex,
    lastPersonalTriggerIndex,
    lastDeescalationIndex,
    lastNonPersonalSafetyReferenceIndex,
    lastContextResetIndex: Math.max(lastDeescalationIndex, lastNonPersonalSafetyReferenceIndex),
    latestNonPersonalSafetyClassification,
    hasThirdPersonReference,
    otherPersonCurrentConcern,
    hasFirstPersonHistoricalReference,
  };
}

function getSafetyContextWindow(messages = [], state = getSafetyContextState(messages)) {
  const entries = Array.isArray(messages) ? messages : [];
  return state.lastContextResetIndex >= 0
    ? entries.slice(state.lastContextResetIndex + 1)
    : entries;
}

function hasActivePersonalSafetySignal(messages = []) {
  return getSafetyContextState(messages).activePersonalSafety;
}

function hasRecentImmediateSafetyResponse(messages = []) {
  const entries = Array.isArray(messages) ? messages : [];
  const lastUserIndex = entries.reduce((found, entry, index) => (
    String(entry?.role || entry?.sender || "").toLowerCase() === "user" ? index : found
  ), -1);
  return entries
    .slice(Math.max(0, lastUserIndex - 5), lastUserIndex)
    .filter((entry) => String(entry?.role || entry?.sender || "").toLowerCase() === "assistant")
    .some((entry) => /\b(?:pause the coaching|immediate safety|are you safe right now|feel unsafe right now)\b/i.test(textFromMessage(entry)));
}

function hasExplicitSafetyDeescalation(messages = []) {
  const entries = Array.isArray(messages) ? messages : [];
  const latestUserIndex = entries.reduce((found, entry, index) => (
    String(entry?.role || entry?.sender || "").toLowerCase() === "user" ? index : found
  ), -1);
  if (latestUserIndex < 0) return false;
  const latestUser = entries[latestUserIndex];
  if (!hasExplicitPersonalSafetyClarification(textFromMessage(latestUser))) return false;
  const priorEntries = entries.slice(0, latestUserIndex);
  const priorState = getSafetyContextState(priorEntries);
  const priorSafetyResponse = priorEntries
    .filter((entry) => String(entry?.role || entry?.sender || "").toLowerCase() === "assistant")
    .some((entry) => /\b(?:pause the coaching|immediate safety|are you safe right now|feel unsafe right now)\b/i.test(textFromMessage(entry)));
  return Boolean(
    priorSafetyResponse ||
    priorState.lastPersonalTriggerIndex >= 0 ||
    priorState.hasThirdPersonReference ||
    priorState.hasFirstPersonHistoricalReference
  );
}

function hasPracticalContextClarification(text = "") {
  const normalized = normalizeSafetyText(text);
  if (!normalized) return false;
  return PRACTICAL_CLARIFICATION_PATTERN.test(normalized) ||
    PRACTICAL_CONTEXT_PATTERN.test(normalized) ||
    PRACTICAL_HERE_PATTERN.test(normalized);
}

function textFromMessage(entry = {}) {
  return String(entry?.content || entry?.text || "");
}

function detectCumulativeSafetyAdjacentSignal(messages = [], latestText = "") {
  const userTexts = (Array.isArray(messages) ? messages : [])
    .filter((entry) => String(entry?.role || entry?.sender || "").toLowerCase() === "user")
    .map((entry) => normalizeSafetyText(textFromMessage(entry)))
    .filter(Boolean)
    .slice(-8);
  const latest = normalizeSafetyText(latestText || userTexts[userTexts.length - 1] || "");
  const texts = latest && latest !== userTexts[userTexts.length - 1]
    ? [...userTexts, latest]
    : userTexts;
  const joined = texts.join(" ");
  if (!joined) return null;

  const signals = CUMULATIVE_SAFETY_ADJACENT_SIGNALS
    .filter((entry) => entry.pattern.test(joined))
    .map((entry) => entry.key);

  const holdingRequests = texts.filter((text) =>
    /\b(just sit with|sit with this|sit with it|no moving forward|don'?t move forward|do not move forward|no advice|only reflect|just stay|stay with (?:this|it|the))\b/i.test(text)
  ).length;
  const distinctSignals = new Set(signals);
  const latestSignals = signals.filter((key) => {
    const entry = CUMULATIVE_SAFETY_ADJACENT_SIGNALS.find((item) => item.key === key);
    return entry?.pattern.test(latest);
  });
  const reachedThreshold = distinctSignals.size >= 3 || (holdingRequests >= 2 && distinctSignals.size >= 2);
  if (!reachedThreshold) return null;

  return {
    level: "cumulative_safety_adjacent",
    safetySignal: false,
    ambiguousSafetySignal: false,
    cumulativeSafetyAdjacentSignal: true,
    realHelpSignal: false,
    urgent: false,
    reviewFlagRecommended: true,
    matched: latestSignals[0] || [...distinctSignals][0] || "cumulative_safety_adjacent_distress",
  };
}

exports.classifySafetyReference = classifySafetyReference;
exports.detectCumulativeSafetyAdjacentSignal = detectCumulativeSafetyAdjacentSignal;
exports.getSafetyContextState = getSafetyContextState;
exports.getSafetyContextWindow = getSafetyContextWindow;
exports.hasActivePersonalSafetySignal = hasActivePersonalSafetySignal;
exports.hasExplicitPersonalSafetyClarification = hasExplicitPersonalSafetyClarification;
exports.hasExplicitSafetyDeescalation = hasExplicitSafetyDeescalation;
exports.hasPracticalContextClarification = hasPracticalContextClarification;
exports.hasProtectedSafetyWording = hasProtectedSafetyWording;
exports.hasRecentImmediateSafetyResponse = hasRecentImmediateSafetyResponse;
exports.hasTypoTolerantPassiveRiskSignal = hasTypoTolerantPassiveRiskSignal;
exports.normalizeSafetyText = normalizeSafetyText;
